import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import EnumSelect from 'components/field/EnumSelect';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useLocalStorage from 'hooks/useLocalStorage';

import { ENUMS } from 'services/consts';

import PATHS, { mergePaths } from 'utils/paths';

import { faChevronCircleLeft, faChevronCircleRight, faThumbTack } from '@fortawesome/pro-light-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faThumbTack as faSolidThumbTack } from '@fortawesome/pro-solid-svg-icons';

import * as S from './FeaturesList.styled';
import UpgradePlanModal from './UpgradePlanModal';

const featuresListConfig = [
	{
		title: 'connectShippingContractBasic',
		description: 'connectShippingContractBasicDescription',
		package: 'FreePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/connectShippingContract1.svg',
		featureLink: mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.MORE_CONNECTION),
		isPinned: false
	},
	{
		title: 'boxConfiguration',
		description: 'boxConfigurationDescription',
		package: 'FreePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/boxConfiguration.svg',
		featureLink: PATHS.BOX_SETTINGS,
		isPinned: false
	},
	{
		title: 'PrintSettings',
		description: 'PrintSettingsDescription',
		package: 'FreePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/printSettings.svg',
		featureLink: PATHS.PRINT_SETTINGS,
		isPinned: false
	},
	{
		title: 'salesChannelIntegrationOne',
		description: 'salesChannelIntegrationOneDescription',
		package: 'FreePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/salesChannelIntegration1.svg',
		featureLink: mergePaths(PATHS.SALES_CHANNELS, PATHS.ONLINE_STORES),
		isPinned: false
	},
	{
		title: 'unlimitedWarehouses',
		description: 'unlimitedWarehousesDescription',
		package: 'FreePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/warehouse.svg',
		featureLink: PATHS.WAREHOUSES,
		isPinned: false
	},
	{
		title: 'unlimitedSupportAcrossAllChannels',
		description: 'unlimitedSupportAcrossAllChannelsDescription',
		package: 'FreePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/supportAcressAllChannels.svg',
		featureAction: async () => {
			await window.voiceflow?.chat.open();
			await window.voiceflow?.chat.show();
		},
		isPinned: false
	},
	{
		title: 'codTransferTwiceAMonth',
		description: 'codTransferTwiceAMonthDescription',
		package: 'FreePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/cod.svg',
		featureLink: PATHS.COD_WALLET,
		isPinned: false
	},
	{
		title: 'connectShippingContractThreeCarriers',
		description: 'connectShippingContractThreeCarriersDescription',
		package: 'StarterPackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/connectShippingContract1.svg',
		featureLink: mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.MORE_CONNECTION),
		isPinned: false
	},
	{
		title: 'customaziblePages',
		description: 'customaziblePagesDescription',
		package: 'StarterPackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/customizablePages.svg',
		featureLink: PATHS.CUSTOMIZABLE_PAGES,
		isPinned: false
	},
	{
		title: 'smsModuleLimited',
		description: 'smsModuleLimitedDescription',
		package: 'StarterPackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/smsModuleLimited.svg',
		featureLink: PATHS.SMS_SETTINGS,
		isPinned: false
	},
	{
		title: 'otoFlexDriversApp',
		description: 'otoFlexDriversAppDescription',
		package: 'StarterPackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/otoflex.svg',
		featureLink: PATHS.OTOFLEX_SETTINGS,
		isPinned: false
	},
	{
		title: 'salesChannelIntegrationThree',
		description: 'salesChannelIntegrationThreeDescription',
		package: 'StarterPackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/salesChannelIntegration1.svg',
		featureLink: mergePaths(PATHS.SALES_CHANNELS, PATHS.ONLINE_STORES),
		isPinned: false
	},
	{
		title: 'tenUsers',
		description: 'tenUsersDescription',
		package: 'StarterPackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/10users.svg',
		featureLink: PATHS.USERS,
		isPinned: false
	},
	{
		title: 'brandConfigurationThree',
		description: 'brandConfigurationThreeDescription',
		package: 'StarterPackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/brandConfig.svg',
		featureLink: PATHS.BRANDS,
		isPinned: false
	},
	{
		title: 'connectShippingContractUnlimited',
		description: 'connectShippingContractUnlimitedDescription',
		package: 'ScalePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/connectShippingContract1.svg',
		featureLink: mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.MORE_CONNECTION),
		isPinned: false
	},
	{
		title: 'emailModuleLimited',
		description: 'emailModuleLimitedDescription',
		package: 'ScalePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/emailModuleLimited.svg',
		featureLink: mergePaths(PATHS.EMAIL, PATHS.EMAIL_SETTINGS),
		isPinned: false
	},
	{
		title: 'whatsappModuleLimited',
		description: 'whatsappModuleLimitedDescription',
		package: 'ScalePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/whatsappModuleLimited.svg',
		featureLink: mergePaths(PATHS.OTHER_INTEGRATIONS, PATHS.INTEGRATIONS),
		isPinned: false
	},
	{
		title: 'slaManagement',
		description: 'slaManagementDescription',
		package: 'ScalePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/sla.svg',
		featureLink: mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.CONNECTED_PARTNERS),
		isPinned: false
	},
	{
		title: 'salesChannelIntegrationUnlimited',
		description: 'salesChannelIntegrationUnlimitedDescription',
		package: 'ScalePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/salesChannelIntegration1.svg',
		featureLink: mergePaths(PATHS.SALES_CHANNELS, PATHS.ONLINE_STORES),
		isPinned: false
	},
	{
		title: 'unlimitedUsers',
		description: 'unlimitedUsersDescription',
		package: 'ScalePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/10users.svg',
		featureLink: PATHS.USERS,
		isPinned: false
	},
	{
		title: 'brandConfigurationUnlimited',
		description: 'brandConfigurationUnlimitedDescription',
		package: 'ScalePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/brandConfig.svg',
		featureLink: PATHS.BRANDS,
		isPinned: false
	},
	{
		title: 'codTransferOnceAWeek',
		description: 'codTransferOnceAWeekDescription',
		package: 'ScalePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/cod.svg',
		featureLink: PATHS.COD_WALLET,
		isPinned: false
	},
	{
		title: 'basicShipmentAutomation',
		description: 'basicShipmentAutomationDescription',
		package: 'ScalePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/basicAutomation.svg',
		featureLink: mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.FEASIBILITY_RULES),
		isPinned: false
	},
	{
		title: 'basicApiAccess',
		description: 'basicApiAccessDescription',
		package: 'ScalePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/bacisApiAccess.svg',
		featureLink: '/sales-channels/oto-api',
		isPinned: false
	},
	{
		title: 'basicWms',
		description: 'basicWmsDescription',
		package: 'ScalePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/basicWms.svg',
		featureLink: PATHS.OTOPACK_SETTINGS,
		isPinned: false
	},
	{
		title: 'inventorySync',
		description: 'inventorySyncDescription',
		package: 'EnterprisePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/inventorySync.svg',
		featureLink: mergePaths(PATHS.SALES_CHANNELS, PATHS.CONNECTED_STORES),
		isPinned: false
	},
	{
		title: 'pricesBasedOnContract',
		description: 'pricesBasedOnContractDescription',
		package: 'EnterprisePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/pricesBasedOnContracts.svg',
		featureLink: mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.MORE_CONNECTION),
		isPinned: false
	},
	{
		title: 'emailModule',
		description: 'emailModuleDescription',
		package: 'EnterprisePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/emailModuleLimited.svg',
		featureLink: mergePaths(PATHS.EMAIL, PATHS.EMAIL_SETTINGS),
		isPinned: false
	},
	{
		title: 'whatsappModule',
		description: 'whatsappModuleDescription',
		package: 'EnterprisePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/whatsappModuleLimited.svg',
		featureLink: mergePaths(PATHS.OTHER_INTEGRATIONS, PATHS.INTEGRATIONS),
		isPinned: false
	},
	{
		title: 'shipFromBranch',
		description: 'shipFromBranchDescription',
		package: 'EnterprisePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/shipFromBranch.svg',
		featureLink: PATHS.ORDER_AUTOMATION,
		isPinned: false
	},
	{
		title: 'clickAndCollectModule',
		description: 'clickAndCollectModuleDescription',
		package: 'EnterprisePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/clickAndCollect.svg',
		featureLink: PATHS.LOCALIZATION_SETTINGS,
		isPinned: false
	},
	{
		title: 'otoRetailApp',
		description: 'otoRetailAppDescription',
		package: 'EnterprisePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/otoRetailApp.svg',
		outerLink: 'https://play.google.com/store/apps/details?id=com.tryoto.otoretail',
		isPinned: false
	},
	{
		title: 'advancedShippingAutomation',
		description: 'advancedShippingAutomationDescription',
		package: 'EnterprisePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/advancedShippingAutomation.svg',
		featureLink: mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.FEASIBILITY_RULES),
		isPinned: false
	},
	{
		title: 'advancedApiAccess',
		description: 'advancedApiAccessDescription',
		package: 'EnterprisePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/advancedApiAccess.svg',
		featureLink: '/sales-channels/oto-api',
		isPinned: false
	},
	{
		title: 'advancedWms',
		description: 'advancedWmsDescription',
		package: 'EnterprisePackage',
		image: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/featureIcons/advancedWms.svg',
		featureLink: PATHS.OTOPACK_SETTINGS,
		isPinned: false
	}
];

const FeaturesList = () => {
	const { planName } = useSelector((state) => state.user);

	const [selectedPlan, setSelectedPlan] = useState('allPlans');
	const [pinnedFeatures, setPinnedFeatures] = useLocalStorage('homePinnedFeatures', []);

	return (
		<S.OtoFeaturesSectionWrapper>
			<S.FixedHeader>
				<S.Header>
					<Typography variant="p2" fontWeight="700">
						otoFeatures
					</Typography>
					<Tooltip placement="right" title={'otoFeaturesTooltip'}>
						<Icon icon={faCircleInfo} />
					</Tooltip>
				</S.Header>
				<EnumSelect
					enumName={ENUMS.HOME_PAGE_CLIENT_PLANS}
					multipleSelection={false}
					setValue={(val) => {
						setSelectedPlan(val ?? null);
					}}
					value={selectedPlan}
					valueRemovable={false}
				/>
			</S.FixedHeader>
			<S.FeatureList>
				{pinnedFeatures
					.filter((f) => (selectedPlan === 'allPlans' ? true : selectedPlan === f.package))
					.map((f, index) => (
						<FeatureCard
							key={index}
							feature={f}
							index={index}
							onPinClicked={(e) => {
								e.stopPropagation();
								setPinnedFeatures(pinnedFeatures.filter((fl) => fl.title !== f.title));
							}}
							currentPlan={planName}
							isPinned
						/>
					))}
				{featuresListConfig
					.filter(
						(f) =>
							(selectedPlan === 'allPlans' ? true : selectedPlan === f.package) &&
							!pinnedFeatures.some((pf) => pf.title === f.title)
					)
					.map((f, index) => (
						<FeatureCard
							key={index}
							feature={f}
							index={index}
							onPinClicked={(e) => {
								e.stopPropagation();
								setPinnedFeatures([...pinnedFeatures, f]);
							}}
							currentPlan={planName}
						/>
					))}
			</S.FeatureList>
		</S.OtoFeaturesSectionWrapper>
	);
};

export default FeaturesList;

const FeatureCard = ({ feature, index, onPinClicked, currentPlan, isPinned }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const {
		localization: { dir }
	} = useSelector((state) => state.layout);

	const comparePlans = (plan) => {
		const plans = {
			FreePackage: 1,
			StarterPackage: 2,
			ScalePackage: 3,
			EnterprisePackage: 4
		};

		return plans[currentPlan] >= plans[plan];
	};

	const handleFeatureAction = () => {
		if (comparePlans(feature.package)) {
			if (feature.featureLink) {
				navigate(feature.featureLink);
			} else if (feature.featureAction) {
				feature.featureAction();
			} else if (feature.outerLink) {
				window.open(feature.outerLink, '_blank');
			}
		} else {
			NiceModal.show(UpgradePlanModal, {
				newPlan: feature.package
			});
		}
	};

	return (
		<S.FeatureContainer key={index} onClick={handleFeatureAction} id={feature.title}>
			<Image url={feature.image} alt={t(feature.title)} width="6rem" height="6rem" />
			<S.TextContent>
				<Typography
					variant="p2"
					fontWeight="500"
					noWrap
					css={css`
						line-height: var(--paragraph3-line-height);
					`}>
					{t(feature.title)}
				</Typography>
				<Typography
					variant="p3"
					noWrap
					color="var(--content-3)"
					css={css`
						line-height: var(--paragraph3-line-height);
					`}>
					{t(feature.description)}
				</Typography>
			</S.TextContent>
			<S.ActionButtons>
				<S.PinButton
					size={'x-small'}
					icon={isPinned ? faSolidThumbTack : faThumbTack}
					onClick={onPinClicked}
					variant={isPinned ? 'yellow' : 'greyOutline'}
				/>

				<Icon
					size="large"
					color="var(--black-40)"
					icon={dir === 'rtl' ? faChevronCircleLeft : faChevronCircleRight}
				/>
			</S.ActionButtons>
		</S.FeatureContainer>
	);
};
